import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import moment from "moment";

import { UiAvatar, UiButton, UiCard, UiCardTitle, UiDate, UiIcon, UiCardFooter, UiNavLink } from 'shared/uikit';
import { COLORS, ICONS, ROUTES } from 'shared/constants';
import { useNavigate, useStore } from "shared/hooks";
import { UserModel } from "shared/models";
import { usersBirthdaysQuery } from "shared/queries";
import { CalendarItemTypeEnum } from "shared/enums";

import './styles.scss';

const UserItem = observer(({ user }: { user: UserModel }) => {
    return (
        <UiNavLink to={ROUTES.USER(user.slug)} className='w-users-birthdays-item'>
            <div className="w-users-birthdays-item__avatar">
                <UiAvatar size={40} label={user.previewName} image={user.imageAvatar}/>
            </div>
            <div className="w-users-birthdays-item__inner">
                <div className="w-users-birthdays-item__info">
                    <div className="w-users-birthdays-item__name">{user.previewName}</div>
                    <div className="w-users-birthdays-item__position">{user.userPositionName}</div>
                </div>
                <UiDate value={user.birthdayAt} format={'D MMMM'}/>
            </div>
        </UiNavLink>
    )
});

export const WUsersBirthdaysMonth = observer(() => {
    const navigate = useNavigate();
    const store = useStore(() => ({
        items: [] as UserModel[],
        date: moment(),
        isWeekOpened: false,
        isMonthOpened: false,
        isLoading: true,
        isSuccess: false,
    }));

    const fetchItems = useCallback(async () => {
        store.set('isLoading', true);
        store.set('isSuccess', false);
        const response = await usersBirthdaysQuery();
        if (response.isSuccess && response.data) {
            store.set('items', response.data.items.map(item => new UserModel(item)));
            store.set('isSuccess', true);
        }
        store.set('isLoading', false);
    }, [store]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const usersSorted = useMemo(() => {
        return store.items.slice().sort((a, b) => {
            return +a.birthDayAtMoment.format('MDD') - +b.birthDayAtMoment.format('MDD');
        });
    }, [store.items]);

    const usersMonth = useMemo(() => {
        const month = +moment().format('M');

        return usersSorted
            .filter(user => {
                return +user.birthDayAtMoment.format('M') === month;
            });
    }, [usersSorted]);

    const usersMonthSliced = usersMonth.slice(0, store.isMonthOpened ? 1000 : 3);

    return (
        <UiCard isLoading={store.isLoading} className='w-users-birthdays'>
            <UiCardTitle label='Дни рождения в этом месяце' collapsable={'w-users-birthdays'}/>
            <div className="w-users-birthdays__section">
                <div className='w-users-birthdays__items'>
                    {usersMonthSliced.map(user => <UserItem key={`month.${user.id}`} user={user}/>)}
                </div>
                <UiButton
                    isLink
                    isSmall
                    label={store.isMonthOpened ? 'Свернуть' : 'Развернуть'}
                    iconAfter={
                        <UiIcon
                            color={COLORS.BROWN_1}
                            size={14}
                            icon={store.isMonthOpened ? ICONS.CHEVRON_UP : ICONS.CHEVRON_DOWN}
                        />
                    }
                    onClick={() => store.set("isMonthOpened", !store.isMonthOpened)}
                />
            </div>
        </UiCard>
    );
});
